import {
  Chevron,
  Disclaimer,
  Div,
  EditPenIcon,
  H5,
  Icon,
  P,
  PlusIcon,
  Separator,
  TrashIconRed,
  lightTheme,
  renderDayOfWeekString,
} from '@vgrubs/components';
import {
  MenuAvailabilityHourV2Response,
  MenuAvailabilityV2Response,
  MenuV2Response,
} from 'components/src/types/menu.types';
import { Provider } from 'components/src/types/shared.types';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { isMenuProviderDelivery } from '../../Menu';
import { Collapsible } from '../../MenyStyled';
import { AddEditAvailability } from './AddEditAvailability';
import { AddEditSchedule } from './AddEditSchedule';
import { DeleteAvailabilityModal } from './DeleteAvailabilityModal';
import { DeleteScheduleModal } from './DeleteScheduleModal';
import { convert24HourTo12Hour } from '../../../../helpers/time-helpers';

export const ModifierGroupComponent = ({
  item,
  setLoading,
  setMenu,
  menu,
  index,
}: {
  item: MenuAvailabilityV2Response;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  menu: MenuV2Response;
  index: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref.current) setHeight(ref?.current?.clientHeight);
  }, [menu]);

  const [expanded, setExpanded] = useState(false);

  const [addScheduleModal, setAddScheduleModal] =
    useState<MenuAvailabilityV2Response | null>(null);
  const [editAvailabilityModal, setEditAvailabilityModal] =
    useState<MenuAvailabilityV2Response | null>(null);

  const [deleteModalAvailability, setDeleteModalAvailability] = useState<
    number | null
  >(null);

  const { provider } = useParams() as {
    provider: Provider;
    locationId: string;
    brandId: string;
  };

  async function handleDeleteAvailability() {
    setMenu((prev) => {
      if (!prev) return prev;
      const updated = prev.availability.filter(
        (availability: MenuAvailabilityV2Response) =>
          availability.id !== deleteModalAvailability
      );

      return { ...prev, availability: updated };
    });

    setDeleteModalAvailability(null);
  }

  const isProviderDelivery = isMenuProviderDelivery(provider);

  return (
    <>
      <Div $box $p="16px 24px" $flex $alignCenter $between $mb="8">
        <Div
          $flex
          $alignCenter
          $gap="8px"
          onClick={() => setExpanded((prev) => !prev)}
          $width="100%"
          $pointer
        >
          <P
            $bold
            $m="0"
            //   $color={expanded && lightTheme.white}
          >
            {item.name || `Availability ${index + 1}`}
          </P>
        </Div>
        <Div $flex $alignCenter $between $gap="20px">
          {isProviderDelivery && (
            <Icon
              $shadow
              $pointer
              onClick={() => setEditAvailabilityModal(item)}
            >
              <EditPenIcon />
            </Icon>
          )}
          <Icon
            $pointer
            onClick={() => setDeleteModalAvailability(Number(item?.id))}
          >
            <TrashIconRed />
          </Icon>
          <Icon
            $pointer
            $rotate={expanded ? '360deg' : '180deg'}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <Chevron />
          </Icon>
        </Div>
      </Div>
      <Collapsible expanded={expanded} elementHeight={height}>
        <div ref={ref}>
          {item.hours.map((it: MenuAvailabilityHourV2Response, i: number) => {
            return (
              <ModifierComponent
                item={it}
                key={i}
                availability={item}
                setMenu={setMenu}
                setLoading={setLoading}
                setDeleteModalAvailability={setDeleteModalAvailability}
                index={i}
              />
            );
          })}
          {item.hours.length === 0 ? (
            <P $m="0" $center>
              No modifiers
            </P>
          ) : null}

          <Div
            $flex
            $alignCenter
            $justifyEnd
            $gap="8px"
            $pointer
            onClick={() => setAddScheduleModal(item)}
          >
            <Icon>
              <PlusIcon />
            </Icon>

            <H5 $m="0" $color={lightTheme.primary}>
              Add Another Day
            </H5>
          </Div>
        </div>
      </Collapsible>

      <DeleteAvailabilityModal
        deleteModal={!!deleteModalAvailability}
        closeModal={() => {
          setDeleteModalAvailability(null);
        }}
        handleDeleteAvailability={handleDeleteAvailability}
      />

      <AddEditSchedule
        modalIsOpen={addScheduleModal}
        closeModal={() => {
          setAddScheduleModal(null);
        }}
        setLoading={setLoading}
        setMenu={setMenu}
      />

      <AddEditAvailability
        modalIsOpen={editAvailabilityModal}
        closeModal={() => {
          setEditAvailabilityModal(null);
        }}
        setLoading={setLoading}
        setMenu={setMenu}
        edit
      />
    </>
  );
};

export const ModifierComponent = ({
  item,
  availability,
  setLoading,
  setMenu,
  setDeleteModalAvailability,
  index,
}: {
  item: MenuAvailabilityHourV2Response;
  availability: MenuAvailabilityV2Response;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  setDeleteModalAvailability: Dispatch<SetStateAction<number | null>>;
  index: number;
}) => {
  const [addModal, setAddModal] = useState<MenuAvailabilityV2Response | null>(
    null
  );
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const handleDeleteSchedule = async () => {
    setMenu((prev) => {
      if (!prev) return prev;
      const updated = prev.availability.map((a: MenuAvailabilityV2Response) => {
        if (availability.id === a.id) {
          const updatedHours = [...a.hours];
          updatedHours.splice(index, 1); // 🔥 remove by index

          return {
            ...a,
            hours: updatedHours,
          };
        }
        return a;
      });

      return { ...prev, availability: updated };
    });

    setDeleteModal(false);
  };

  return (
    <>
      <Separator />
      <Div $p="16px 20px" $flex $between $alignCenter>
        <div>
          <Disclaimer $mb="4">{renderDayOfWeekString(item.day)}</Disclaimer>
          <P $m="0">
            {convert24HourTo12Hour(item.startTime)} -{' '}
            {convert24HourTo12Hour(item.endTime)}
          </P>
        </div>

        <Div $flex $alignCenter $between $gap="20px">
          <Icon $pointer onClick={() => setAddModal(availability)}>
            <EditPenIcon />
          </Icon>
          <Icon
            $pointer
            onClick={() => {
              if (availability.hours.length === 1) {
                setDeleteModalAvailability(availability?.id);
              } else {
                setDeleteModal(true);
              }
            }}
          >
            <TrashIconRed />
          </Icon>
        </Div>
      </Div>

      <AddEditSchedule
        modalIsOpen={addModal}
        closeModal={() => {
          setAddModal(null);
        }}
        setLoading={setLoading}
        setMenu={setMenu}
        item={item}
        edit
      />

      <DeleteScheduleModal
        deleteModal={deleteModal}
        closeModal={() => {
          setDeleteModal(false);
        }}
        handleDeleteSchedule={handleDeleteSchedule}
      />
    </>
  );
};
